import { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { ShieldCheck, ChartLineUp, Buildings } from "@phosphor-icons/react";

export default function Login() {
  const { login } = useAuth();
  const [userId, setUserId] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!userId || !password) { toast.error("Enter your User ID and password"); return; }
    setBusy(true);
    try {
      await login(userId.trim(), password);
      window.location.href = "/";
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Invalid User ID or password");
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen w-full grid lg:grid-cols-2 bg-zinc-50">
      <div className="hidden lg:flex flex-col justify-between bg-brand-dark text-indigo-100 p-12 relative overflow-hidden">
        <div className="absolute inset-0 opacity-[0.06]" style={{ backgroundImage: "radial-gradient(#fff 1px, transparent 1px)", backgroundSize: "22px 22px" }} />
        <div className="relative z-10 flex items-center gap-3">
          <div className="bg-white rounded-md px-3 py-2 inline-flex items-center">
            <img src="/paisapaid-logo.webp" alt="PaisaPaid Business ERP" className="h-8 w-auto" />
          </div>
        </div>
        <div className="relative z-10 space-y-8">
          <h1 className="font-heading text-4xl leading-tight tracking-tight">
            One clean console for <span className="text-[#AEB8F0]">Disbursements</span>, teams &amp; <span className="text-[#ff9d6e]">Own Brand</span> sales.
          </h1>
          <div className="space-y-5 text-sm text-indigo-200">
            <Feature icon={<ChartLineUp size={20} weight="duotone" />} title="Simple disbursement flow" desc="Lead source, team, commission, GST/TDS — all in one form." />
            <Feature icon={<ShieldCheck size={20} weight="duotone" />} title="Role-based access" desc="Backend & OB Sales executives see only what they should." />
            <Feature icon={<Buildings size={20} weight="duotone" />} title="Clean masters" desc="Products, lenders, agents and partners — organised." />
          </div>
        </div>
        <div className="relative z-10 text-xs text-indigo-300 tracking-widest uppercase">Business ERP · India</div>
      </div>

      <div className="flex items-center justify-center p-8">
        <form onSubmit={submit} className="w-full max-w-sm">
          <div className="lg:hidden mb-10">
            <img src="/paisapaid-logo.webp" alt="PaisaPaid Business ERP" className="h-9 w-auto" />
          </div>
          <p className="text-xs uppercase tracking-widest text-zinc-500 mb-2">Welcome back</p>
          <h2 className="font-heading text-3xl tracking-tight text-zinc-900 mb-6">Sign in</h2>
          <div className="space-y-4">
            <div>
              <Label className="text-xs text-zinc-500 mb-1.5 block">User ID</Label>
              <Input data-testid="login-userid" value={userId} onChange={(e) => setUserId(e.target.value)} className="rounded-sm h-11" placeholder="Your User ID" autoFocus />
            </div>
            <div>
              <Label className="text-xs text-zinc-500 mb-1.5 block">Password</Label>
              <Input data-testid="login-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} className="rounded-sm h-11" placeholder="••••••••" />
            </div>
            <Button data-testid="login-submit" type="submit" disabled={busy} className="w-full h-11 rounded-sm bg-brand hover:bg-brand-dark">
              {busy ? "Signing in…" : "Sign in"}
            </Button>
          </div>
          <p className="text-xs text-zinc-400 mt-6 leading-relaxed">Your administrator creates and manages accounts. Contact your admin if you can't sign in.</p>
        </form>
      </div>
    </div>
  );
}

const Feature = ({ icon, title, desc }) => (
  <div className="flex gap-3">
    <div className="text-[#AEB8F0] mt-0.5">{icon}</div>
    <div><div className="text-white font-medium">{title}</div><div className="text-indigo-300">{desc}</div></div>
  </div>
);
